import ContactModal from 'Douleutaras/functional-components/ccf/ContactModal';
import {
  CCFModalSlugs,
  FormattedMilestone, JobBidsStatusBuyer, MilestonesData, ReduxEntity
} from 'Douleutaras/types/index';
import CancelModal from 'Douleutaras/functional-components/ccf/CancelModal';
import NeedHelpModal from 'Douleutaras/functional-components/ccf/NeedHelpModal';
import {
  AllModalOptions,
  ContactModalOptions,
  MODAL_BY_MILESTONE_POSITION, NEED_HELP_MILESTONES,
  SelectedModalArgs, SelectionContactModalOptions, SolveIssueModalOptions
} from 'Douleutaras/constants/ccf/need-help-options';
import ExtraBidModal from 'Douleutaras/functional-components/ccf/ExtraBidModal';
import moment from 'moment';
import React, { Dispatch, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { URLS } from 'Douleutaras/constants/urls';
import { getNeedHelpSave } from 'Douleutaras/redux/data/ccf/needHelpOptionsSlice';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import WhatHappenedModal from 'Douleutaras/functional-components/ccf/WhatHappenedModal';
import SelectionContactModal from 'Douleutaras/functional-components/ccf/SelectionContactModal';
import StartingDateModal from 'Douleutaras/functional-components/ccf/StartingDateModal';
import SelectMilestone from 'Douleutaras/functional-components/ccf/SelectMilestone';
import QuoteDetailsModal from 'Douleutaras/functional-components/ccf/QuoteDetailsModal';
import { bidBuyerStatusEndpoint, createBidBuyerStatus, updateBidBuyerStatus } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import { useProGenderInfo } from 'Douleutaras/hooks/users/pro';
import SolveIssuesModal from 'Douleutaras/functional-components/ccf/SolveIssuesModal';
import { useSellerAttributes } from 'Douleutaras/hooks/users/sellerAttributes';
import { ExtraBidSource, useAB } from 'Douleutaras/app/lib/ab';

export enum CustomerFlowSlug {
  NEED_HELP = 'need_help',
  POSITIVE = 'positive',
  DECLARE_INTEREST = 'declare_interest',
  CANCEL_JOB = 'cancel_job',
  SOLVE_ISSUES = 'solve_issues'
}

export enum CCFModalType {
  JOB = 'job',
  BID = 'bid'
}

export interface CCFModalsProps {
  bidId?: string;
  conversationId?: string;
  currentMilestone?: FormattedMilestone;
  initialScreen: CustomerFlowSlug;
  jobCategoryId?: string;
  jobId?: string;
  jobTitle?: string;
  mostProgressedMilestoneStep?: number;
  sellerPhoneNumber?: string;
  sellerId?: string;
  showBackButton?: boolean;
  type: CCFModalType;
  totalBidsCount?: number;
  onBack?(): void;
  onClose(openFlag: boolean): void;
  onUpdateStatusSuccess?(): void;
}

// Updates / Creates buyer status (submits a form)
export const updateStatus = async (milestoneKey: NEED_HELP_MILESTONES,
                                   jobId: string,
                                   bidId: string,
                                   dispatch: Dispatch<{}>,
                                   bidBuyerStatus?: ReduxEntity<JobBidsStatusBuyer, MilestonesData>,
                                   price?: string,
                                   jobStartDate?: string,
                                   priceDefiningAnswers?: { [index in string]: string }
  ) => {
  const transformPriceDefiningAnswers = () => {
    return Object.keys(priceDefiningAnswers)
      .map(questionName => {
        const answerId = questionName.replace('questionId_', '');
        const answerValue = priceDefiningAnswers[questionName];

        return {
          questionId: parseInt(answerId, 0),
          answerValueId: parseInt(answerValue, 0)
        };
      });
  };

  const metaKey = bidBuyerStatusEndpoint(jobId, bidId);
  const priceDefiningAnswersTransformed = priceDefiningAnswers && Object.keys(priceDefiningAnswers).length > 0 ?
    transformPriceDefiningAnswers()
    : [];

  // Check if the buyer status entry already exists and either create or update it
  const actionAttributes = {
    jobId,
    bidId,
    milestone: milestoneKey,
    price,
    priceDefiningAnswers: priceDefiningAnswersTransformed,
    jobStartDate: moment(jobStartDate)
      .format('YYYY-MM-DD')
  };

  if (typeof bidBuyerStatus?.meta?.[metaKey] !== 'undefined' && bidBuyerStatus.meta[metaKey].data.length > 0) {
    return dispatch(updateBidBuyerStatus({
      ...actionAttributes,
      id: bidBuyerStatus.meta[metaKey].data[0].id
    }));
  } else {
    return dispatch(createBidBuyerStatus(actionAttributes));
  }
};

export const CCFModals =
  ({
    bidId,
    conversationId,
    currentMilestone,
    initialScreen,
    jobCategoryId,
    jobId,
    jobTitle,
    mostProgressedMilestoneStep,
    sellerPhoneNumber,
    sellerId,
    totalBidsCount,
    type,
    onClose,
    onUpdateStatusSuccess
  }: CCFModalsProps) => {
  const { captureJobCancelClicked } = useAB();

  const [ activeNeedHelpModal, setActiveNeedHelpModal ] = useState<SelectedModalArgs>({
    optionSlug: undefined,
    modalSlug: undefined,
    modalOptions: {}}
  );
  const [ lastNeedHelpModalStack, setLastNeedHelpModalStack ] = useState<Array<SelectedModalArgs>>([]);

  // Local form data state (react-hook-form replacement for passing across modals)
  const [ selectedMilestone, setSelectedMilestone ] = useState<NEED_HELP_MILESTONES | undefined>(undefined);
  const [ quotedPrice, setQuotedPrice ] = useState<string | undefined>(undefined);
  const [ priceDefiningAnswers, setPriceDefiningAnswers ] = useState<Record<string, string> | undefined>(undefined);

  // Open initial screen
  const [ modalOpen, setModalOpen ] = useState(initialScreen);

  useEffect(() => {
    setModalOpen(initialScreen);
  }, [ initialScreen ]);

  const bidBuyerStatus = useAppSelector(state => state.jobBidsStatusBuyer);

  const dispatch = useAppDispatch();
  const currentMilestoneOption = MODAL_BY_MILESTONE_POSITION[currentMilestone?.position];

  // IF it's a bid, use its current milestone slug, if it's a job, use the most positive milestone
  const journeyStage = type === CCFModalType.BID ? currentMilestoneOption : MODAL_BY_MILESTONE_POSITION[mostProgressedMilestoneStep];

  // Reports the click and sets the history stack for back navigation and sets "setActiveNeedHelpModal"
  const onNeedHelpOptionSelected = async ({ modalSlug, optionSlug, modalOptions }: SelectedModalArgs,
                                          saveReporting: boolean = true) => {
    setModalOpen(undefined);
    // TEMPORARY: Opens support page when "help" is clicked, until a support modal is created
    if (modalSlug === CCFModalSlugs.SUPPORT || modalSlug === CCFModalSlugs.WITH_NONE) {
      await dispatch(getNeedHelpSave({ jobId,
        bidId,
        buyerChoice: optionSlug,
        journeyStage,
        customerFlow: CustomerFlowSlug.NEED_HELP
      }));
      window.location.href = URLS.SUPPORT.CONTACT; // Redirect to contact modal

      return;
    }

    if (modalSlug === CCFModalSlugs.CANCEL) {
      // @AB PostHog event
      captureJobCancelClicked(jobId, ExtraBidSource.CCF_NEED_HELP);
    }

    if (saveReporting) {
      // Save user selection to API
      dispatch(getNeedHelpSave({
        jobId,
        bidId,
        buyerChoice: optionSlug,
        journeyStage,
        customerFlow: CustomerFlowSlug.NEED_HELP
      }));
    }
    const activeNeedHelpModalOpts = { optionSlug, modalSlug, modalOptions };

    // Open the corresponding modal
    setActiveNeedHelpModal(activeNeedHelpModalOpts);

    if (lastNeedHelpModalStack.find(stack => stack.modalSlug === modalSlug) === undefined) {
      setLastNeedHelpModalStack([ ...lastNeedHelpModalStack, activeNeedHelpModalOpts ]);
    }
  };

  const goBack = lastCCFJourneyStack => () => {
    // Close window if history trace is less than 2
    if (lastCCFJourneyStack.length < 2) {
      setActiveNeedHelpModal(undefined);
      setModalOpen(initialScreen);
    } else {
      setActiveNeedHelpModal(lastCCFJourneyStack[lastCCFJourneyStack.length - 2]);
    }
    setLastNeedHelpModalStack(lastCCFJourneyStack.slice(0, -1));
  };

  // Some options contain sellers to be selected instead of options., ie WITH_PRO or WITH_NONE
  const onSellerSelected = ({ extraBidConversationId, extraBidSellerPhoneNumber, extraBidSellerName, extraBidSellerId, optionSlug }:
                                      { extraBidConversationId: string,
                                        extraBidSellerPhoneNumber: string,
                                        extraBidSellerName: string,
                                        extraBidSellerId?: string,
                                        optionSlug: AllModalOptions
                                      }) => {
    const activeModalConf = {
      modalSlug: CCFModalSlugs.EXTRA_BID_CONTACT,
      optionSlug,
      extraModalOptions: {
        conversationId: extraBidConversationId,
        sellerPhoneNumber: extraBidSellerPhoneNumber,
        extraBidSellerName,
        extraBidSellerId
      },
      modalOptions: {}
    };
    dispatch(getNeedHelpSave({
      jobId,
      bidId,
      buyerChoice: optionSlug,
      journeyStage,
      sellerId: extraBidSellerId,
      customerFlow: initialScreen
    }));
    setActiveNeedHelpModal(activeModalConf);
    if (lastNeedHelpModalStack.find(stack => stack.modalSlug === CCFModalSlugs.EXTRA_BID_CONTACT) === undefined) {
      setLastNeedHelpModalStack([...lastNeedHelpModalStack, activeModalConf]);
    }
  };

  const onDeclareInterestSellerSelected = ({ extraBidConversationId,
                                            extraBidSellerPhoneNumber,
                                            extraBidSellerName,
                                            extraBidSellerId,
                                            optionSlug }:
                                      { extraBidConversationId: string,
                                        extraBidSellerPhoneNumber: string,
                                        extraBidSellerName: string,
                                        extraBidSellerId?: string,
                                        optionSlug: AllModalOptions
                                      }) => {
    const activeModalConf = {
      modalSlug: CCFModalSlugs.EXTRA_BID_CONTACT,
      optionSlug,
      extraModalOptions: {
        conversationId: extraBidConversationId,
        sellerPhoneNumber: extraBidSellerPhoneNumber,
        extraBidSellerName,
        extraBidSellerId
      },
      modalOptions: {}
    };
    dispatch(getNeedHelpSave({
      jobId,
      bidId,
      buyerChoice: optionSlug,
      journeyStage,
      sellerId: extraBidSellerId,
      customerFlow: CustomerFlowSlug.DECLARE_INTEREST
    }));
    setActiveNeedHelpModal(activeModalConf);
    setModalOpen(undefined);
  };

  const onCCFModalOptionSelected = ({ optionSlug }: { optionSlug: AllModalOptions }) => {
    const needHelpSaveActionConfigured = getNeedHelpSave({
      jobId,
      bidId,
      buyerChoice: optionSlug,
      journeyStage,
      customerFlow: initialScreen
    });

    if (lastNeedHelpModalStack.find(stack => stack.optionSlug === activeNeedHelpModal.optionSlug) === undefined) {
      setLastNeedHelpModalStack([...lastNeedHelpModalStack, activeNeedHelpModal ]);
    }

    return dispatch(needHelpSaveActionConfigured);
  };

  const modalUserSelection = (userSelection: AllModalOptions) => {
    onCCFModalOptionSelected({
      optionSlug: userSelection
    });
  };

  // Called for each modal.Resets the current modal state and also closes the modal using the callback prop
  const closeModalCallBack = () => {
    // Resets the current position of the need help modal
    setActiveNeedHelpModal(undefined);
    // Dismiss callback coming from outside
    onClose(false);
  };

  const sellerGenderInfo = useProGenderInfo(sellerId);
  const { mugshot, defaultLogo } = useSellerAttributes(sellerId);

  return (
    <>
      { /*Contact modal*/ }
      <ContactModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.CONTACT}
                    options={activeNeedHelpModal?.modalOptions}
                    conversationId={conversationId}
                    sellerPhoneNumber={sellerPhoneNumber}
                    title={<Trans values={sellerGenderInfo}>T.CCF.CONTACT MODAL TITLE</Trans>}
                    note={<Trans values={sellerGenderInfo}>T.CCF.CONTACT MODAL BODY</Trans>}
                    setIsOpen={closeModalCallBack}
                    onBack={goBack(lastNeedHelpModalStack)}
                    onOptionSelected={modalUserSelection}
                    jobId={jobId}
                    bidId={bidId}
                    onCCFModalOptionSelected={onCCFModalOptionSelected}
                    showBackButton
                    sellerId={sellerId}
      />
      { /* Cancel CCF Modal */ }
      <CancelModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.CANCEL || modalOpen === CustomerFlowSlug.CANCEL_JOB}
                   options={activeNeedHelpModal?.modalOptions}
                   jobId={jobId}
                   jobTitle={jobTitle}
                   userMugshot={mugshot}
                   setIsOpen={closeModalCallBack}
                   showBackButton={lastNeedHelpModalStack?.length > 0}
                   source={modalOpen === CustomerFlowSlug.CANCEL_JOB ? ExtraBidSource.JOB_PAGE : ExtraBidSource.CCF_NEED_HELP}
                   onBack={goBack(lastNeedHelpModalStack)}
                   onOptionSelected={modalUserSelection}
                   title={<Trans values={sellerGenderInfo}>T.CCF.CANCEL MODAL MODAL TITLE</Trans>}
                   titleOnSuccess={<Trans values={{ ...sellerGenderInfo, jobTitle }}>
                     T.CCF.CANCEL MODAL MODAL TITLE SUCCESS {{jobTitle}}</Trans>}
      />

      { /* Need help modal flow */ }
      <NeedHelpModal isOpen={modalOpen === CustomerFlowSlug.NEED_HELP || activeNeedHelpModal?.modalSlug === CCFModalSlugs.NEED_HELP}
                     setIsOpen={onClose}
                     onOptionSelected={onNeedHelpOptionSelected}
                     jobId={jobId}
                     bidId={bidId}
                     currentMilestone={currentMilestone}
                     mostProgressedMilestoneStep={mostProgressedMilestoneStep}
                     totalBidsCount={totalBidsCount}
                     type={type}
                     onBack={goBack(lastNeedHelpModalStack)}
                     showBackButton={lastNeedHelpModalStack?.length > 0}
                     userMugshot={mugshot}
                     title={<Trans values={sellerGenderInfo}>T.CCF.NEED HELP MODAL TITLE</Trans>}
      />
      { /* Declare interest journey initial screen */ }
      <SelectionContactModal isOpen={modalOpen === CustomerFlowSlug.DECLARE_INTEREST}
                             options={{
                               [SelectionContactModalOptions.WITH_PRO]: {
                                 active: true
                               }
                             }}
                             onProContactSelected={onDeclareInterestSellerSelected}
                             onNoProSelected={onNeedHelpOptionSelected}
                             setIsOpen={closeModalCallBack}
                             jobId={jobId}
                             title={<Trans values={sellerGenderInfo}>T.CCF.DECLARE INTEREST MODAL.TITLE</Trans>}
                             showBackButton={false}
                             userMugshot={mugshot}
      />
      { /* What happened? modal flow */ }
      <WhatHappenedModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.WHAT_HAPPENED}
                         options={activeNeedHelpModal?.modalOptions}
                         onOptionSelected={onNeedHelpOptionSelected}
                         onCCFModalOptionSelected={onCCFModalOptionSelected}
                         onBack={goBack(lastNeedHelpModalStack)}
                         setIsOpen={closeModalCallBack}
                         jobId={jobId}
                         jobCategoryId={jobCategoryId}
                         title={<Trans values={sellerGenderInfo}>T.CCF.WHAT HAPPENED.MODAL TITLE</Trans>}
                         userMugshot={mugshot}
      />
      { /* Pro selection modal flow */ }
      <SelectionContactModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.SELECT_CONTACT}
                             options={activeNeedHelpModal?.modalOptions}
                             onNoProSelected={onNeedHelpOptionSelected}
                             onProContactSelected={onSellerSelected}
                             onBack={goBack(lastNeedHelpModalStack)}
                             setIsOpen={closeModalCallBack}
                             jobId={jobId}
                             title={<Trans>T.CCF.SELECT CONTACT MODAL.TITLE</Trans>}
                             userMugshot={mugshot}
      />
      { /* SolveIssuesModal */ }
      <SolveIssuesModal
        isOpen={modalOpen === CustomerFlowSlug.SOLVE_ISSUES}
        jobId={jobId}
        options={{
          [SolveIssueModalOptions.I_HAVE_AN_ISSUE]: {
            active: true
          },
          [SolveIssueModalOptions.I_WANT_TO_PROCEED_HAVE_PROCEEDED]: {
            active: true
          },
          [SolveIssueModalOptions.I_WONT_PROCEED_WITH_THE_JOB]: {
            active: true
          }
        }}
        setIsOpen={closeModalCallBack}
        showBackButton={false}
        title={<Trans>T.CCF.SOLVE ISSUES MODAL.TITLE</Trans>}
        userMugshot={defaultLogo}
        onOptionSelected={onNeedHelpOptionSelected}
        saveReport={({ optionSlug }: { optionSlug: SolveIssueModalOptions }) => dispatch(getNeedHelpSave({
          jobId,
          bidId,
          buyerChoice: optionSlug,
          journeyStage,
          customerFlow: CustomerFlowSlug.NEED_HELP
        }))}
      />
      { /* Extra Bid Main modal */ }
      <ExtraBidModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.EXTRA_BID}
                     title={<Trans values={sellerGenderInfo}>T.CCF.EXTRA BID MODAL TITLE</Trans>}
                     note={<Trans values={sellerGenderInfo}>T.CCF.EXTRA BID MODAL BODY</Trans>}
                     onBack={goBack(lastNeedHelpModalStack)}
                     setIsOpen={closeModalCallBack}
                     options={activeNeedHelpModal?.modalOptions}
                     jobId={jobId}
                     sellerId={sellerId}
                     onExtraBidOptionSelected={onSellerSelected}
                     onCCFModalOptionSelected={onCCFModalOptionSelected}
                     userMugshot={mugshot}
      />
      { /* Extra Bid -> Contact PRO Modal */ }
      <ContactModal isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.EXTRA_BID_CONTACT}
                    options={{
                      [ContactModalOptions.I_WILL_CALL_THE_PRO]: {
                        active: true
                      },
                      [ContactModalOptions.I_WILL_USE_THE_CHAT]: {
                        active: true
                      }
                    }}
                    conversationId={activeNeedHelpModal?.extraModalOptions?.conversationId}
                    sellerPhoneNumber={activeNeedHelpModal?.extraModalOptions?.sellerPhoneNumber}
                    sellerId={activeNeedHelpModal?.extraModalOptions?.extraBidSellerId}
                    setIsOpen={closeModalCallBack}
                    onBack={goBack(lastNeedHelpModalStack)}
                    title={<Trans values={sellerGenderInfo}>T.CCF.EXTRA BID MODAL CONTACT TITLE</Trans>}
                    note={<Trans values={sellerGenderInfo}>T.CCF.EXTRA BID MODAL CONTACT BODY</Trans>}
                    onOptionSelected={modalUserSelection}
                    jobId={jobId}
                    bidId={bidId}
                    onCCFModalOptionSelected={onCCFModalOptionSelected}
                    showBackButton
      />
      { /* Status update: Starting date (Not implemented yet) */ }
      <SelectMilestone
        isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.SELECT_MILESTONE}
        title={<Trans values={sellerGenderInfo}>T.CCF.SELECT MILESTONE MODAL.TITLE</Trans>}
        setIsOpen={closeModalCallBack}
        jobId={jobId}
        bidId={bidId}
        currentMilestone={currentMilestone}
        onBack={goBack(lastNeedHelpModalStack)}
        onSelectMilestoneCallback={ (milestoneKey: NEED_HELP_MILESTONES) => {
          setSelectedMilestone(milestoneKey);

          const hasQuoteDetailModal = [
            NEED_HELP_MILESTONES.OFFER_MILESTONE,
            NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
            NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
          ].includes(milestoneKey);

          // Only in selected milestones, show the next wizard item
          if (hasQuoteDetailModal) {
            onNeedHelpOptionSelected({
              optionSlug: undefined,
              modalSlug: CCFModalSlugs.QUOTE_DETAILS,
              modalOptions: {}
            }, false);
          } else {
            // Submit the form
            return updateStatus(milestoneKey, jobId, bidId, dispatch, bidBuyerStatus)
              .then(closeModalCallBack)
              .finally(onUpdateStatusSuccess);
          }
        }}
        userMugshot={mugshot}
      />
      <QuoteDetailsModal
        isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.QUOTE_DETAILS}
        title={<Trans values={sellerGenderInfo}>T.CCF.QUOTE DETAILS TITLE</Trans>}
        note={<Trans values={sellerGenderInfo}>T.CCF.QUOTE DETAILS BODY</Trans>}
        bidId={bidId}
        jobId={jobId}
        setIsOpen={closeModalCallBack}
        onBack={goBack(lastNeedHelpModalStack)}
        onSubmit={({ price, priceDefiningAnswers: pda }) => {
          // Persist values locally
          setQuotedPrice(price);
          setPriceDefiningAnswers(pda);

          // Show "Date Select Modal" when on the 5th milestone
          const hasDateModal = [
            NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE
          ].includes(selectedMilestone);

          if (hasDateModal) {
            onNeedHelpOptionSelected(
              {
                optionSlug: undefined,
                modalSlug: CCFModalSlugs.SELECT_STARTING_DATE,
                modalOptions: {}
              }, false
            );
          } else {
            return updateStatus(selectedMilestone as NEED_HELP_MILESTONES,
              jobId,
              bidId,
              dispatch,
              bidBuyerStatus,
              price,
              undefined,
              pda
            )
              .then(closeModalCallBack)
              .finally(onUpdateStatusSuccess);
          }
        }}
        userMugshot={mugshot}
      />
      <StartingDateModal
        jobId={jobId}
        bidId={bidId}
        isOpen={activeNeedHelpModal?.modalSlug === CCFModalSlugs.SELECT_STARTING_DATE}
        title={<Trans values={sellerGenderInfo}>T.CCF.STARTING DATE.TITLE</Trans>}
        setIsOpen={closeModalCallBack}
        onBack={goBack(lastNeedHelpModalStack)}
        onSubmit={({ jobStartDate }) =>
          updateStatus(selectedMilestone,
            jobId,
            bidId,
            dispatch,
            bidBuyerStatus,
            quotedPrice,
            jobStartDate,
            priceDefiningAnswers
          )
            .then(closeModalCallBack)
            .then(onUpdateStatusSuccess)
        }
        userMugshot={mugshot}
      />
    </>
  );
};

export default CCFModals;
